import useLocalStorage from '@rehooks/local-storage';
import { useQuery } from 'react-query';
import { formatLocalStorageKey } from '../utils';
import { NAVIGATION_ENDPOINTS } from '../constants/urls';
import { AppsV1NavigationMenuItem } from '../types';
import { useAppsmithSolutionsFlag } from '../flags';

export type AppsmithSolution = {
  id: string;
  app: string;
  url: string;
  public: boolean;
};

export const useAppsmithSolutions = () => {
  const key = formatLocalStorageKey('ZNavAppsmithSolutions');

  const [cachedSolutions, setCachedSolutions] =
    useLocalStorage<AppsmithSolution[]>(key);

  const [enabled] = useAppsmithSolutionsFlag();

  const results = useQuery({
    queryKey: [key],
    queryFn: async () => {
      const data = await window.fetch(NAVIGATION_ENDPOINTS.APPSMITH_SOLUTIONS, {
        method: 'GET',
      });

      if (data.status !== 200) {
        throw new Error('No data from API.');
      }

      const body = (await data.json()) as AppsmithSolution[];

      return body;
    },
    onSuccess: (data) => {
      setCachedSolutions(data);
    },
    initialData: () => {
      if (cachedSolutions) {
        return cachedSolutions;
      }
    },
    enabled,
  });

  return {
    ...results,
    /**
     * Adds Appsmith solution to menu items if there are extensions available.
     * This will create the "Zuora/Extensions" menu item if it does not exist.
     */
    addSolutionMenuItems: (menus: AppsV1NavigationMenuItem[]) => {
      if (!results.data) {
        return menus;
      }

      const solutionMenuGroupIndex =
        menus?.findIndex((item) => item.id === 'Zuora/Extensions') ?? -1;

      const parentMenuItem: AppsV1NavigationMenuItem = {
        id: 'Zuora/Extensions',
        label: 'Extensions',
        url: null,
        parentId: null,
        parentLabel: null,
      };

      // .filter((extension) => extension.public)
      const menuItems: AppsV1NavigationMenuItem[] = results.data.map(
        (extension) => {
          return {
            id: `Zuora/${extension.navOption}/${extension.id}`,
            label: extension.app,
            url: extension.url,
            parentId: `Zuora/${extension.navOption}`,
            parentLabel: `${extension.navOption}`,
          };
        },
      );

      if (solutionMenuGroupIndex === -1) {
        return [...menus, parentMenuItem, ...menuItems];
      }

      const start = menus.slice(0, solutionMenuGroupIndex);
      const end = menus.slice(solutionMenuGroupIndex);

      return [...start, ...menuItems, ...end];
    },
  };
};
